/**
 * Salas de LiveKit abiertas de más (monitoreo/sesionesLivekit).
 *
 * Cada minuto que una sala sigue abierta se factura. El monitor revisa LiveKit
 * cada pocos minutos y deja aquí las salas que superan el umbral. Esas mismas
 * salas generan alertas normales (LIVEKIT_SESION_LARGA y LIVEKIT_SESION_LARGA_AJENA)
 * que ya aparecen en el tablero: esta sección da el detalle, no las duplica.
 */
import { Check, Clock, Video } from 'lucide-react';
import { useDocument } from '../../hooks/useFirestore';
import { PANAMA_TZ, aFecha, type SesionesLivekitDoc } from './costosUtil';
import styles from './SesionesLivekitSeccion.module.css';

const horaPanama = (d: Date | null) =>
  d ? d.toLocaleString('es-PA', { timeZone: PANAMA_TZ, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;

const estadoLegible = (e: string) => e.replace(/_/g, ' ');

export default function SesionesLivekitSeccion() {
  const { data, loading, error } = useDocument<SesionesLivekitDoc>('monitoreo', 'sesionesLivekit');

  if (error) {
    console.warn('[SesionesLivekitSeccion] no se pudo leer monitoreo/sesionesLivekit', error);
    return null;
  }

  const umbral = data?.umbralMin ?? 60;
  const salas = [...(data?.salas ?? [])].sort((a, b) => b.edadMin - a.edadMin);
  const ultima = horaPanama(aFecha(data?.actualizadoEn));

  return (
    <section className={styles.seccion} aria-labelledby="sesiones-livekit-titulo">
      <h2 id="sesiones-livekit-titulo" className={styles.titulo}>
        <Video size={16} aria-hidden="true" /> Sesiones de LiveKit abiertas de más
      </h2>

      {loading ? (
        <p className={styles.vacio}>Cargando…</p>
      ) : !data ? (
        <p className={styles.vacio}>Todavía no hay una revisión del monitor de sesiones.</p>
      ) : salas.length === 0 ? (
        <p className={styles.tranquilo}>
          <Check size={16} aria-hidden="true" />
          <span>
            Ninguna sala lleva abierta más de {umbral} min.
            {ultima && <> Última revisión: {ultima}.</>}
          </span>
        </p>
      ) : (
        <>
          <p className={styles.resumen}>
            {salas.length === 1 ? '1 sala lleva' : `${salas.length} salas llevan`} abierta más de {umbral} min y
            siguen facturando minutos.
            {ultima && <> Última revisión: {ultima}.</>}
          </p>
          <ul className={styles.lista}>
            {salas.map((s) => {
              const critica = s.edadMin > umbral * 2;
              return (
                <li key={s.nombre} className={`${styles.sala} ${critica ? styles.critica : ''}`}>
                  <span className={styles.nombre} title={s.nombre}>{s.nombre}</span>
                  <span className={styles.edad}>
                    <Clock size={12} aria-hidden="true" />
                    <strong>{Math.round(s.edadMin)} min</strong> abierta
                    {critica && <span className={styles.chip}>más del doble del umbral</span>}
                  </span>
                  <span className={styles.gente}>
                    {s.humanos} {s.humanos === 1 ? 'persona' : 'personas'} · {s.agentes} {s.agentes === 1 ? 'agente' : 'agentes'}
                  </span>
                  <span className={styles.consulta}>
                    {s.desconocida || s.consultaEstado == null
                      ? <em>sin consulta en este entorno</em>
                      : <>consulta: {estadoLegible(s.consultaEstado)}</>}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
